// Generated by tutorials/record.cjs from tutorials/articles/h36-census.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h36 --text-only` (wording) or
// `node tutorials/record.cjs h36` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'census',
  catalogId: 'h36',
  title: 'The census',
  summary: 'Census shows who is in your facility right now: active residents in total and unit by unit, discharged and deleted residents, the guests eating with you today, and a history of who was served at each meal.',
  audience: 'Dietary managers and dietary staff who plan meals by head count',
  area: 'residents',
  learn: [
    'Read the census totals: active, discharged and deleted',
    'See how many residents each unit has',
    'Count today\'s guests, meal by meal',
    'Find residents in the detail list',
    'What changes the census: admissions, discharges and the EHR',
    'Look back at who was served at each meal in History',
  ],
  keywords: [
    'census',
    'head count',
    'headcount',
    'resident count',
    'how many residents',
    'active residents',
    'discharged',
    'deleted',
    'inactive',
    'by unit',
    'unit count',
    'guests today',
    'guest count',
    'census history',
    'history',
    'tray ticket count',
    'selective menu count',
    'tt count',
    'sm count',
    'who was served',
  ],
  steps: [
    {
      id: 'open',
      part: 'Read the census',
      heading: 'Open Census',
      text: 'In the menu, select Residents, then Census.\n\nCensus Summary, at the top, counts your residents. Total Active Residents is everyone in the facility now: not discharged and not deleted. Discharged counts the residents who have been discharged and are still on file, and Deleted / Inactive the ones that were deleted.',
      media: {
        image: {
          src: '/help/census/open.webp',
          width: 1280,
          height: 180,
        },
        clip: {
          src: '/help/census/open.mp4',
          width: 1280,
          height: 180,
          durationSec: 2,
        },
        alt: 'The Census page: Census Summary with Total Active Residents 32, Discharged 1 and Deleted / Inactive 0.',
      },
    },
    {
      id: 'units',
      part: 'Read the census',
      heading: 'Active residents by unit',
      text: 'Active Residents by Unit has a card for each unit with how many active residents it has. A resident with no unit is counted under Unassigned.\n\nThe cards add up to Total Active Residents.',
      media: {
        image: {
          src: '/help/census/units.webp',
          width: 1280,
          height: 158,
        },
        alt: 'Active Residents by Unit: Birch 11, Maple 11 and Willow 10.',
      },
    },
    {
      id: 'guests',
      part: 'Read the census',
      heading: 'Guests Today',
      text: 'Guests Today counts the guests who eat with you today, worked out from their schedules: the total, then how many at each meal. A guest who comes for lunch and dinner counts once in Total and once at each of those meals.\n\nGuests are not part of the resident totals above. See all guests opens the Guests page.',
      media: {
        image: {
          src: '/help/census/guests.webp',
          width: 1280,
          height: 190,
        },
        alt: 'Guests Today: Total 2, Breakfast 1, Lunch 1 and Dinner 1, with the See all guests link.',
      },
    },
    {
      id: 'detail',
      part: 'Read the census',
      heading: 'The detail list',
      text: 'Scroll down to Active Residents Detail: every active resident with their unit, room, bed, diet order and admit date. Select a column heading to sort by it.\n\nType in the search box to find a resident by name, or everyone on a unit or with a diet order. A line under the list says how many of the active residents it is showing.',
      media: {
        image: {
          src: '/help/census/detail.webp',
          width: 1280,
          height: 570,
        },
        clip: {
          src: '/help/census/detail.mp4',
          width: 1280,
          height: 570,
          durationSec: 4.8,
        },
        alt: 'Active Residents Detail filtered to Birch: eleven residents with Unit Birch, their rooms, beds and diet orders, and Showing 11 of 32 active residents.',
      },
    },
    {
      id: 'count',
      part: 'Read the census',
      heading: 'Count again',
      text: 'The line at the bottom of the page counts every resident on file, the active ones and the units.\n\nTo count again after someone is admitted or discharged, select Refresh, the circled arrow at the top right.',
      media: {
        image: {
          src: '/help/census/count.webp',
          width: 1280,
          height: 928,
        },
        alt: 'The bottom line: 33 total residents, 32 active, 3 units, and the Refresh button at the top right of the page.',
      },
    },
    {
      id: 'feeds',
      part: 'What changes the census',
      heading: 'What changes the numbers',
      text: 'Census has nothing to type in. It counts your resident list, so the numbers change when the list does:\n\n- Admitting a resident adds them to Total Active Residents and to their unit.\n- Discharging a resident moves them from active to Discharged. Readmitting them brings them back.\n- Moving a resident to another unit moves them to that unit\'s card.\n- Deleting a resident moves them to Deleted / Inactive. Manage Residents, under Residents, can restore them.\n- If your facility is connected to its EHR, the admissions, discharges and moves your EHR sends change the census on their own, usually within a few minutes.\n\nGuests Today follows the guests\' schedules.',
      media: {
        image: {
          src: '/help/census/feeds.webp',
          width: 1280,
          height: 180,
        },
        alt: 'Census Summary: Total Active Residents 32, Discharged 1 and Deleted / Inactive 0.',
      },
    },
    {
      id: 'history',
      part: 'Look back with History',
      heading: 'Open History',
      text: 'Select History at the top of the page. The census history lists a row for each resident and guest at each meal, written when their tray ticket or selective menu was printed.\n\nIt opens on the last month: Start Date a month ago, End Date today. The number beside Census History counts the rows.',
      media: {
        image: {
          src: '/help/census/history.webp',
          width: 1280,
          height: 692,
        },
        clip: {
          src: '/help/census/history.mp4',
          width: 1280,
          height: 692,
          durationSec: 2,
        },
        alt: 'The Maple Grove Care Center Census window: Census History with Start Date and End Date, a search box, and rows for each resident at Breakfast, Lunch and Dinner.',
      },
    },
    {
      id: 'columns',
      part: 'Look back with History',
      heading: 'Read a row',
      text: 'Date For is the day of the meal, and Date/Time Generated when it was printed. Day is the menu cycle day and Menu Name the menu it came from. Diet Types and Consistencies are the diet order on the ticket.\n\nTT Count is how many tray tickets were printed for that meal, and SM Count how many selective menus. A check in G marks a guest.',
      media: {
        image: {
          src: '/help/census/columns.webp',
          width: 1280,
          height: 692,
        },
        alt: 'Census History rows with Date/Time Generated, Date For, G, Name, Day, Mealtime, Menu Name, Diet Types, Consistencies, TT Count and SM Count.',
      },
    },
    {
      id: 'search',
      part: 'Look back with History',
      heading: 'Find one resident',
      text: 'Type a name in the search box to see one resident\'s meals. Searching for a family name shows the resident and their guests together, with a check in G for each guest.\n\nChange Start Date and End Date to look at other days.',
      media: {
        image: {
          src: '/help/census/search.webp',
          width: 1280,
          height: 692,
        },
        clip: {
          src: '/help/census/search.mp4',
          width: 1280,
          height: 692,
          durationSec: 4.7,
        },
        alt: 'Census History searched for Chen: CHEN, MEI at each meal and CHEN, LINDA at breakfast with a check in G.',
      },
    },
  ],
  tips: [
    'Your dashboard\'s Census Summary card shows the same active total, with the counts by diet type and by unit.',
    'The search box above the detail list also finds a unit or a diet order: type Birch, or Renal.',
    'In History, a check in the G column marks a guest. Search for a family name to see a resident and their guests together.',
  ],
  notes: [
    'The census counts residents, not guests. Guests have their own Guests Today numbers.',
    'Census needs the Facility area on your role, and Guests Today shows only when your role can see residents. Ask your administrator.',
    'History\'s More actions menu holds Delete All Census History. It removes every census record for your facility and cannot be undone, so it is kept for roles with full Facility access.',
  ],
  related: [
    'admit-resident',
    'discharge-readmit',
    'guests',
    'your-dashboard',
    'tray-tickets',
    'ehr-connection',
  ],
  recorded: '2026-11-03',
};

export default article;
